import { LogOut, Package, ClipboardCheck, Users, Building2 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "../contexts/AuthContext";

const roleBadge = {
  salesperson: "bg-[#c9a578] text-[#1d3a14]",
  coordinator: "bg-[#e6c01e] text-[#1d3a14]",
  billing: "bg-[#264a1a] text-[#faf6ea]",
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  if (!user) return null;

  const linkCls = (path) =>
    `px-3 py-1.5 rounded-md text-sm transition-colors ${
      loc.pathname === path
        ? "bg-[#faf6ea]/20 text-[#faf6ea]"
        : "text-[#e8dcc0] hover:bg-[#faf6ea]/10 hover:text-[#faf6ea]"
    }`;

  return (
    <header className="paddy-stripe text-[#faf6ea] no-print" data-testid="navbar">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-home-link">
          <div className="bg-[#faf6ea] rounded-lg p-1 shadow-md">
            <img
              src="/brand/ramajeyam-logo.jpg"
              alt="Ramajeyam Rice"
              className="h-10 w-10 object-contain rounded"
              data-testid="brand-logo"
            />
          </div>
          <div>
            <div className="brand-serif text-2xl font-bold leading-none">
              Ramajeyam Rice
            </div>
            <div className="text-[10px] tracking-widest uppercase text-[#e8dcc0]">
              RMJ Sales Order System
            </div>
          </div>
        </Link>

        <nav className="flex items-center gap-1 flex-wrap" data-testid="nav-links">
          {user.role === "salesperson" && (
            <>
              <Link to="/" className={linkCls("/")} data-testid="nav-new-order">
                <Package className="inline h-4 w-4 mr-1" />
                New Order
              </Link>
              <Link to="/orders" className={linkCls("/orders")} data-testid="nav-my-orders">
                <ClipboardCheck className="inline h-4 w-4 mr-1" />
                My Orders
              </Link>
            </>
          )}
          {(user.role === "coordinator" || user.role === "billing") && (
            <Link to="/" className={linkCls("/")} data-testid="nav-all-orders">
              <ClipboardCheck className="inline h-4 w-4 mr-1" />
              All Orders
            </Link>
          )}
          {user.role === "coordinator" && (
            <Link to="/new" className={linkCls("/new")} data-testid="nav-new-order-coord">
              <Package className="inline h-4 w-4 mr-1" />
              New Order
            </Link>
          )}
          {user.is_admin && (
            <Link to="/users" className={linkCls("/users")} data-testid="nav-users">
              <Users className="inline h-4 w-4 mr-1" />
              Users
            </Link>
          )}
          <Link
            to="/customers"
            className={linkCls("/customers")}
            data-testid="nav-customers"
          >
            <Building2 className="inline h-4 w-4 mr-1" />
            Customers
          </Link>
        </nav>

        <div className="flex items-center gap-3" data-testid="user-chip">
          <div className="text-right leading-tight hidden sm:block">
            <div className="text-sm font-medium">{user.name || user.email}</div>
            <div className="text-[10px] opacity-80">{user.email}</div>
          </div>
          <span
            className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${roleBadge[user.role] || ""}`}
            data-testid="user-role-badge"
          >
            {user.role}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={logout}
            data-testid="logout-button"
            className="bg-transparent border-[#faf6ea]/40 text-[#faf6ea] hover:bg-[#faf6ea]/15 hover:text-[#faf6ea]"
          >
            <LogOut className="h-4 w-4 mr-1" />
            Logout
          </Button>
        </div>
      </div>
    </header>
  );
}
